import { ChangeEvent, forwardRef, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CSSProp } from "styled-components";
import { ArrowIcon } from "../../assets/icons/ArrowIcon";
import { currentYear } from "../../lib/dayjs/utils";

import { SelectBox } from "../atoms/SelectBox";

const startYear = currentYear - 120;
const yearOptions = [...Array(currentYear - startYear)]
  .map((_, index) => ({
    label: `${startYear + index + 1}年`,
    value: startYear + index + 1,
  }))
  .reverse();
const monthOptions = [...Array(12)].map((_, index) => ({
  label: `${index + 1}月`,
  value: index + 1,
}));
const dayOptions = [...Array(31)].map((_, index) => ({
  label: `${index + 1}日`,
  value: index + 1,
}));

const Caret = () => (
  <label css={{ transform: "rotate(-90deg)" }}>
    <ArrowIcon tw="svg text-white" css={{ transform: "scale(0.8)" }} />
  </label>
);

export interface DateSelectProps {
  name: string;
  yearSelectCss?: CSSProp<any>;
  monthSelectCss?: CSSProp<any>;
  daySelectCss?: CSSProp<any>;
  caret?: boolean;
}

export const DateSelect = forwardRef<Element, DateSelectProps>(
  ({ name, yearSelectCss, monthSelectCss, daySelectCss, caret }, _ref) => {
    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");
    const [day, setDay] = useState("");

    const form = useFormContext();

    const formValue = form.watch(name);
    const [formYear, formMonth, formDay]: string[] = formValue
      ? formValue.split("-")
      : [];

    useEffect(() => {
      const nextValue = year && month && day ? `${year}-${month}-${day}` : null;

      if (nextValue && formValue !== nextValue) {
        form.setValue(name, nextValue, { shouldValidate: true });
      }
    }, [year, month, day, formValue, form, name]);

    // local stateの年月日が存在しない場合、watchしているReactHookFormの値を初期値として設定する
    useEffect(() => {
      if (!year && formYear) {
        setYear(formYear);
      }
      if (!month && formMonth) {
        formMonth.match(/0\d/)
          ? setMonth(formMonth.slice(1))
          : setMonth(formMonth);
      }
      if (!day && formDay) {
        formDay.match(/0\d/) ? setDay(formDay.slice(1)) : setDay(formDay);
      }
    }, [formYear, formMonth, formDay, year, month, day]);

    const handleChangeYear = (event: ChangeEvent<HTMLSelectElement>) => {
      setYear(event.target.value);
    };
    const handleChangeMonth = (event: ChangeEvent<HTMLSelectElement>) => {
      setMonth(event.target.value);
    };
    const handleChangeDay = (event: ChangeEvent<HTMLSelectElement>) => {
      setDay(event.target.value);
    };

    return (
      <div
        tw="w-full grid grid-flow-col grid-cols-3"
        css={{ gridColumnGap: 8 }}
      >
        <SelectBox
          tw="w-auto"
          css={yearSelectCss}
          name={`${name}-year`}
          label="年"
          options={yearOptions}
          onChange={handleChangeYear}
          value={year}
          endAdornment={caret && <Caret />}
        />
        <SelectBox
          tw="w-auto"
          css={monthSelectCss}
          name={`${name}-month`}
          label="月"
          options={monthOptions}
          onChange={handleChangeMonth}
          value={month}
          endAdornment={caret && <Caret />}
        />
        <SelectBox
          tw="w-auto"
          css={daySelectCss}
          name={`${name}-day`}
          label="日"
          options={dayOptions}
          onChange={handleChangeDay}
          value={day}
          endAdornment={caret && <Caret />}
        />
      </div>
    );
  }
);

DateSelect.displayName = "DateSelect";
